import * as ModularSofa from '../modularSofa';
import { AffineTransform3D, AxisAlignedBoundingBox, OrientedBoundingBox, Position3D, Vector3D, } from '@fixtuur/fine';
export function buildRules(modularTemplate) {
    const sharedRules = [
        validateMinSize(1),
        validateMaxSize(12),
        validateNoIntersect(modularTemplate),
    ];
    const ruleBuilders = {
        validateNoAdjacentCorners: validateNoAdjacentCorners(modularTemplate),
        validateMax2Corners: validateMaxCorners(modularTemplate, 2),
        validateMax3Corners: validateMaxCorners(modularTemplate, 3),
        validateEnds: validateEnds(modularTemplate),
        validateEndsWithoutOttoman: validateEndsWithoutOttoman(modularTemplate),
        validateCornersAsEnds: validateCornersAsEnds(modularTemplate),
        validateChaiseNotCornerAdjacent: validateChaiseNotCornerAdjacent(modularTemplate),
        validateNoEnclosedCornerSequence: validateNoEnclosedCornerSequence(modularTemplate),
    };
    const rulesets = {
        standard: [
            'validateNoAdjacentCorners',
            'validateMax2Corners',
            'validateEnds',
            'validateChaiseNotCornerAdjacent',
        ],
        relaxed: ['validateMax3Corners', 'validateCornersAsEnds', 'validateEnds'],
        freeform: [],
    };
    const buildRules = modularTemplate.buildRules ?? rulesets[modularTemplate.ruleset];
    const validators = [...sharedRules, ...buildRules.map(buildRule => ruleBuilders[buildRule])];
    return validateAllBuildRules(validators);
}
export function finalisationRules(modularTemplate) {
    if (!modularTemplate.finalisationRules || modularTemplate.finalisationRules.length < 1)
        return () => [];
    const ruleBuilders = {
        validateCornersConnected: validateCornersConnected(modularTemplate),
    };
    const validators = [
        ...modularTemplate.finalisationRules.map(finalisationRule => ruleBuilders[finalisationRule]),
    ];
    return validateAllFinalisationRules(validators);
}
function validateAllBuildRules(validations) {
    return pieces => validations.every(validate => validate(pieces));
}
function validateAllFinalisationRules(validations) {
    return pieces => validations.flatMap(validate => validate(pieces));
}
function validateMinSize(minPieces) {
    return pieces => pieces.length >= minPieces;
}
function validateMaxSize(maxPieces) {
    return pieces => pieces.length <= maxPieces;
}
function validateMaxCorners(modularOptions, maxCorners) {
    return pieces => {
        return (pieces.filter(piece => {
            const [pieceType] = ModularSofa.getModuleById(modularOptions, piece);
            return pieceType === 'connecting-corner';
        }).length <= maxCorners);
    };
}
export function validateCornersAsEnds(modularOptions) {
    return pieces => {
        const end = pieces.length - 1;
        return (pieces.filter((piece, position) => {
            const [pieceType] = ModularSofa.getModuleById(modularOptions, piece);
            return pieceType === 'connecting-corner' && !(position === 0 || position === end);
        }).length <= 1);
    };
}
function validateNoAdjacentCorners(modularOptions) {
    return pieces => {
        const corners = pieces.map(piece => {
            const [pieceType] = ModularSofa.getModuleById(modularOptions, piece);
            return pieceType === 'connecting-corner';
        });
        return !corners.slice(1).some((a, position) => corners[position] && a);
    };
}
function getPieceTypes(modularOptions, pieces) {
    return pieces.map(piece => {
        const [pieceType] = ModularSofa.getModuleById(modularOptions, piece);
        return pieceType;
    });
}
function validateEndTypes(pieceTypes, leftEndPieces, rightEndPieces) {
    const allEndPieces = leftEndPieces.concat(rightEndPieces);
    const end = pieceTypes.length - 1;
    return pieceTypes.every((pieceType, position) => {
        return ((position === 0 && leftEndPieces.includes(pieceType)) ||
            (position === end && rightEndPieces.includes(pieceType)) ||
            !allEndPieces.includes(pieceType));
    });
}
function validateEnds(modularOptions) {
    return pieces => {
        const leftEndPieces = ['left-end', 'left-end-chaise', 'ottoman'];
        const rightEndPieces = ['right-end', 'right-end-chaise', 'ottoman'];
        const pieceTypes = getPieceTypes(modularOptions, pieces);
        return validateEndTypes(pieceTypes, leftEndPieces, rightEndPieces);
    };
}
function validateEndsWithoutOttoman(modularOptions) {
    return pieces => {
        const leftEndPieces = ['left-end', 'left-end-chaise'];
        const rightEndPieces = ['right-end', 'right-end-chaise'];
        const pieceTypes = getPieceTypes(modularOptions, pieces);
        return validateEndTypes(pieceTypes, leftEndPieces, rightEndPieces);
    };
}
function validateChaiseNotCornerAdjacent(modularOptions) {
    return pieces => {
        const cornerOrChaise = pieces.map(piece => {
            const [pieceType] = ModularSofa.getModuleById(modularOptions, piece);
            return (pieceType === 'connecting-corner' ||
                pieceType === 'left-end-chaise' ||
                pieceType === 'right-end-chaise');
        });
        return !cornerOrChaise.slice(1).some((a, position) => cornerOrChaise[position] && a);
    };
}
function validateNoEnclosedCornerSequence(modularOptions) {
    return pieces => {
        const corner = pieces.map(piece => {
            const [pieceType] = ModularSofa.getModuleById(modularOptions, piece);
            return pieceType === 'connecting-corner';
        });
        let runLength = 0;
        let maxRunLength = 0;
        for (let i = 0; i < corner.length; ++i) {
            if (corner[i]) {
                runLength++;
            }
            else {
                runLength = 0;
            }
            maxRunLength = Math.max(maxRunLength, runLength);
            if (runLength > 3)
                break;
        }
        return maxRunLength < 3 || (maxRunLength === 3 && pieces.length === 3);
    };
}
function validateNoIntersect(modularOptions) {
    return pieces => {
        const placedPieces = ModularSofa.placePieceSequence(pieces, modularOptions);
        const pieceBounds = placedPieces.map(placed => {
            return new OrientedBoundingBox(new AxisAlignedBoundingBox(placed.bounds.min.translate(new Vector3D([0.02, 0.02, 0.02])), placed.bounds.max.translate(new Vector3D([-0.02, -0.02, -0.02]))), new AffineTransform3D(placed.orientation.matrix(), placed.position.subtract(new Position3D(0, 0, 0))));
        });
        return pieceBounds.every((bounds, i) => {
            return pieceBounds.every((otherBounds, j) => {
                if (i === j || i + 1 === j || i - 1 === j)
                    return true;
                return !bounds.isCollidingWith(otherBounds);
            });
        });
    };
}
function validateCornersConnected(modularOptions) {
    return pieces => {
        const startPiece = pieces[0];
        const endPiece = pieces[pieces.length - 1];
        const [startPieceType] = ModularSofa.getModuleById(modularOptions, startPiece);
        const [endPieceType] = ModularSofa.getModuleById(modularOptions, endPiece);
        return startPieceType !== 'connecting-corner' && endPieceType !== 'connecting-corner'
            ? []
            : ['validateCornersConnected'];
    };
}
